<template>
  <div>
    <div class="flex items-center justify-between">
      <div class="text-black-victor font-bold text-base select-none">

      </div>
      <button
        class="text-white bg-blue-450 rounded text-xs font-semibold px-2 py-1"
        @click="openTextOverlayModal()"
      >
        +
      </button>
    </div>

    <div class="mt-2 max-h-64 overflow-y-auto">
      <div
        v-if="!textOverlays.length"
        class="text-xs text-gray-500"
      >
        No text overlays yet.
      </div>
      <div
        v-for="(overlay, index) in textOverlays"
        :key="overlay.id || index"
        class="flex items-center justify-between bg-gray-100 rounded px-2 py-2 mb-2"
      >
        <div class="text-xs">
          <div class="font-semibold text-gray-800">
            {{ overlay.name || 'Untitled' }}
          </div>
          <div class="text-gray-600 truncate">
            {{ overlay.text }}
          </div>
        </div>
        <div class="flex gap-2">
          <button
            class="text-blue-450 text-xs font-semibold"
            @click="openTextOverlayModal(overlay, index)"
          >
            Edit
          </button>
          <button
            class="text-red-500 text-xs font-semibold"
            @click="removeTextOverlay(index)"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isTextOverlayModalOpen"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded w-11/12 max-w-5xl p-4">
        <div class="flex justify-between items-center mb-3">
          <div class="text-black-victor font-bold text-base">
            Text Overlay
          </div>
          <button @click="closeTextOverlayModal">
            <i class="pi pi-times cursor-pointer text-gray-600 text-lg" />
          </button>
        </div>

        <div class="flex gap-4">
          <div class="w-2/3">
            <div
              ref="textOverlayPreview"
              class="relative bg-black rounded overflow-hidden w-full"
              style="aspect-ratio: 16 / 9;"
              :class="[isTextOverlayPickerActive ? 'cursor-crosshair' : 'cursor-default']"
            >
              <VideoPlayer
                reference="textOverlayPreviewVideo"
                @loadeddata="onPreviewLoaded"
              />
              <div
                class="absolute inset-0"
                :class="[
                  isTextOverlayPickerActive ? 'cursor-crosshair pointer-events-auto' : 'cursor-default pointer-events-none'
                ]"
                @click="onTextOverlayPick"
              />
              <div
                class="absolute pointer-events-none"
                :style="textOverlayPreviewStyle"
              >
                {{ textOverlayDraft.text || 'Sample Text' }}
              </div>
            </div>
            <div class="text-xs text-gray-500 mt-2">
              Click the picker, then click on the preview to set X/Y position.
            </div>
          </div>

          <div class="w-1/3">
            <div class="grid grid-cols-2 gap-2">
              <label class="text-xs font-semibold text-gray-victor">Label</label>
              <input
                v-model="textOverlayDraft.name"
                type="text"
                class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
              />

              <label class="text-xs font-semibold text-gray-victor">Text</label>
              <input
                v-model="textOverlayDraft.text"
                type="text"
                class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
              />

              <label class="text-xs font-semibold text-gray-victor">Font Family</label>
              <select
                v-model="textOverlayDraft.font_family"
                class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
              >
                <option
                  v-for="font in fontOptions"
                  :key="font"
                  :value="font"
                >
                  {{ font }}
                </option>
              </select>

              <label class="text-xs font-semibold text-gray-victor">Font Size (px)</label>
              <input
                v-model.number="textOverlayDraft.font_size"
                type="number"
                min="1"
                class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
              />

              <div class="col-span-2">
                <div class="text-xs font-semibold text-gray-victor">
                  Appearance
                </div>
                <AppearanceRange
                  v-model:model="textOverlayInterval"
                  :duration="maxOverlayTime"
                  :getPreviewCurrentTime="getPreviewCurrentTime"
                />
              </div>
            </div>

            <div class="mt-3 grid grid-cols-2 gap-2">
              <label class="text-xs font-semibold text-gray-victor">Text Color</label>
              <input
                v-model="textOverlayDraft.text_color"
                type="color"
                class="w-full h-7 bg-white"
              />

              <label class="text-xs font-semibold text-gray-victor">Text Alpha</label>
              <input
                v-model.number="textOverlayDraft.text_alpha"
                type="range"
                min="0"
                max="100"
                class="w-full"
              />

              <label class="text-xs font-semibold text-gray-victor">Background Color</label>
              <input
                v-model="textOverlayDraft.bg_color"
                type="color"
                class="w-full h-7 bg-white"
              />

              <label class="text-xs font-semibold text-gray-victor">Background Alpha</label>
              <input
                v-model.number="textOverlayDraft.bg_alpha"
                type="range"
                min="0"
                max="100"
                class="w-full"
              />
            </div>

            <div class="mt-3">
              <div class="text-xs font-semibold text-gray-victor mb-1">
                Position
              </div>
              <div class="p-2 py-1 bg-gray-lightGrey">
                <div
                  v-for="(chunk,index) in watermarkPositions"
                  :key="index"
                  class="flex justify-around my-1"
                >
                  <input
                    v-for="(item, itemIndex) in chunk"
                    :key="itemIndex"
                    v-model="textOverlayDraft.position_anchor"
                    type="radio"
                    name="text_overlay_position"
                    :value="item"
                    class="text-blue-450 focus:ring-0 focus:border-0 w-3.5 h-3.5 border-gray-300 appearance-none checked:bg-blue-450 focus:shadow-none focus:ring-offset-0"
                  >
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2 mt-2">
                <label class="text-xs font-semibold text-gray-victor">X (px)</label>
                <input
                  v-model.number="textOverlayDraft.position_x"
                  type="number"
                  min="0"
                  class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
                />

                <label class="text-xs font-semibold text-gray-victor">Y (px)</label>
                <input
                  v-model.number="textOverlayDraft.position_y"
                  type="number"
                  min="0"
                  class="bg-gray-100 border-0 rounded px-2 py-1 text-xs"
                />
              </div>

              <button
                class="mt-2 text-xs font-semibold text-white bg-blue-450 rounded px-2 py-1"
                @click="toggleTextOverlayPicker"
              >
                {{ isTextOverlayPickerActive ? 'Cancel Picker' : 'Pick on Preview' }}
              </button>
            </div>

            <div class="flex gap-2 mt-4">
              <button
                class="text-white bg-amber-500 rounded font-bold text-xs px-3 py-2"
                @click="saveTextOverlay"
              >
                Save
              </button>
              <button
                class="text-white bg-gray-400 rounded font-bold text-xs px-3 py-2"
                @click="closeTextOverlayModal"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VideoPlayer from "../VideoPlayer"
import AppearanceRange from "./AppearanceRange"

const FONT_OPTIONS = [
  'Alexandria-Bold.ttf',
  'Alexandria-Regular.ttf',
  'Amiri-BoldItalic.ttf',
  'Amiri-Bold.ttf',
  'Amiri-Italic.ttf',
  'Amiri-Regular.ttf',
  'Beiruti.ttf',
  'Cairo-Bold.ttf',
  'Cairo-Regular.ttf',
  'Rubik-BoldItalic.ttf',
  'Rubik-Bold.ttf',
  'Rubik-Italic.ttf',
  'Rubik-Regular.ttf',
  'Tahoma Bold.ttf',
  'Tahoma Faux Italic.ttf',
  'Tahoma Regular.ttf'
]

export default {
  name: 'TextOverlaysTab',
  components: {
    VideoPlayer,
    AppearanceRange
  },
  props: {
    textOverlays: {
      type: Array,
      default () {
        return []
      }
    },
    videoDuration: {
      type: Number,
      default () {
        return 0
      }
    },
    watermarkPositions: {
      type: Array,
      required: true
    }
  },
  emits: ['update:textOverlays'],
  data: function () {
    return {
      isTextOverlayModalOpen: false,
      isTextOverlayPickerActive: false,
      editingTextOverlayIndex: null,
      textOverlayDraft: this.defaultDraft(),
      fontOptions: FONT_OPTIONS,
      previewVideoWidth: 0,
      previewVideoHeight: 0,
      previewDisplayScaleX: 1,
      previewDisplayScaleY: 1,
      previewDisplayOffsetX: 0,
      previewDisplayOffsetY: 0,
      previewPlayer: null
    }
  },
  computed: {
    textOverlayPreviewStyle () {
      const textAlpha = Math.max(0, Math.min(100, Number(this.textOverlayDraft.text_alpha)))
      const bgAlpha = Math.max(0, Math.min(100, Number(this.textOverlayDraft.bg_alpha)))
      const scaledX = Math.round((this.textOverlayDraft.position_x || 0) * this.previewDisplayScaleX + this.previewDisplayOffsetX)
      const scaledY = Math.round((this.textOverlayDraft.position_y || 0) * this.previewDisplayScaleY + this.previewDisplayOffsetY)
      const fontScale = this.previewDisplayScaleX || 1
      const fontSize = Math.max(1, Math.round((this.textOverlayDraft.font_size || 24) * fontScale))
      const paddingX = Math.max(1, Math.round(4 * fontScale))
      const paddingY = Math.max(1, Math.round(2 * fontScale))
      const fontFamily = this.normalizeFontFamily(this.textOverlayDraft.font_family)
      return {
        left: `${scaledX}px`,
        top: `${scaledY}px`,
        color: this.toRgba(this.textOverlayDraft.text_color, textAlpha),
        backgroundColor: this.toRgba(this.textOverlayDraft.bg_color, bgAlpha),
        fontFamily: fontFamily || 'Arial',
        fontSize: `${fontSize}px`,
        padding: `${paddingY}px ${paddingX}px`,
        borderRadius: '2px',
        whiteSpace: 'pre'
      }
    },
    maxOverlayTime () {
      return Number(this.videoDuration || 0)
    },
    textOverlayInterval: {
      get () {
        const startTime = Number(this.textOverlayDraft.start_time || 0)
        const endTime = Number(this.textOverlayDraft.end_time || 0)
        return [startTime, endTime]
      },
      set (val) {
        const nextStart = Number((val && val[0]) || 0)
        const nextEnd = Number((val && val[1]) || 0)
        this.textOverlayDraft.start_time = nextStart
        this.textOverlayDraft.end_time = nextEnd
      }
    }
  },
  mounted () {
    this.ensureFontFaces()
    window.addEventListener('resize', this.updatePreviewDisplayMetrics)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.updatePreviewDisplayMetrics)
  },
  methods: {
    defaultDraft () {
      const duration = Number(this.videoDuration || 0)
      return {
        id: null,
        name: '',
        text: '',
        font_family: FONT_OPTIONS[0] || 'Arial',
        font_size: 24,
        text_color: '#ffffff',
        text_alpha: 100,
        bg_color: '#000000',
        bg_alpha: 0,
        position_anchor: ['top', 'left'],
        position_x: 0,
        position_y: 0,
        start_time: 0,
        end_time: duration
      }
    },
    openTextOverlayModal (overlay, index) {
      if (overlay) {
        this.textOverlayDraft = JSON.parse(JSON.stringify(overlay))
        this.textOverlayDraft.font_family = this.normalizeFontFamily(this.textOverlayDraft.font_family)
        if (this.textOverlayDraft.end_time === undefined || this.textOverlayDraft.end_time === null) {
          this.textOverlayDraft.end_time = Number(this.videoDuration || 0)
        }
        if (this.textOverlayDraft.start_time === undefined || this.textOverlayDraft.start_time === null) {
          this.textOverlayDraft.start_time = 0
        }
        this.editingTextOverlayIndex = index
      } else {
        this.textOverlayDraft = this.defaultDraft()
        this.textOverlayDraft.id = `to_${Date.now()}`
        this.editingTextOverlayIndex = null
      }
      this.isTextOverlayModalOpen = true
      this.$nextTick(() => {
        this.updatePreviewDisplayMetrics()
      })
    },
    closeTextOverlayModal () {
      this.isTextOverlayModalOpen = false
      this.isTextOverlayPickerActive = false
    },
    saveTextOverlay () {
      const overlayCopy = JSON.parse(JSON.stringify(this.textOverlayDraft))
      delete overlayCopy.font_bold
      delete overlayCopy.font_italic
      const duration = Number(this.videoDuration || 0)
      if (Number.isNaN(overlayCopy.start_time)) {
        overlayCopy.start_time = 0
      }
      if (Number.isNaN(overlayCopy.end_time) || overlayCopy.end_time === 0) {
        overlayCopy.end_time = duration
      }
      if (duration && overlayCopy.end_time > duration) {
        overlayCopy.end_time = duration
      }
      if (overlayCopy.start_time < 0) {
        overlayCopy.start_time = 0
      }
      if (overlayCopy.end_time < overlayCopy.start_time) {
        overlayCopy.end_time = overlayCopy.start_time
      }
      const overlays = this.textOverlays.slice()
      if (this.editingTextOverlayIndex === null || this.editingTextOverlayIndex === undefined) {
        overlays.push(overlayCopy)
      } else {
        overlays.splice(this.editingTextOverlayIndex, 1, overlayCopy)
      }
      this.$emit('update:textOverlays', overlays)
      this.closeTextOverlayModal()
    },
    removeTextOverlay (index) {
      const overlays = this.textOverlays.slice()
      overlays.splice(index, 1)
      this.$emit('update:textOverlays', overlays)
    },
    toggleTextOverlayPicker () {
      this.isTextOverlayPickerActive = !this.isTextOverlayPickerActive
    },
    onTextOverlayPick (event) {
      if (!this.isTextOverlayPickerActive) {
        return
      }
      const videoEl = this.getPreviewVideoEl()
      if (!videoEl) {
        return
      }
      const videoRect = videoEl.getBoundingClientRect()
      const xInVideo = event.clientX - videoRect.left
      const yInVideo = event.clientY - videoRect.top
      if (xInVideo < 0 || yInVideo < 0 || xInVideo > videoRect.width || yInVideo > videoRect.height) {
        return
      }
      const scaleX = this.previewVideoWidth > 0 ? this.previewVideoWidth / videoRect.width : 1
      const scaleY = this.previewVideoHeight > 0 ? this.previewVideoHeight / videoRect.height : 1
      const x = Math.max(0, Math.round(xInVideo * scaleX))
      const y = Math.max(0, Math.round(yInVideo * scaleY))
      this.textOverlayDraft.position_x = x
      this.textOverlayDraft.position_y = y
    },
    onPreviewLoaded (player) {
      this.previewPlayer = player
      this.updatePreviewVideoSizeFromPlayer(player)
      this.$nextTick(() => {
        this.updatePreviewDisplayMetrics()
      })
    },
    getPreviewVideoEl () {
      if (this.previewPlayer && this.previewPlayer.el) {
        const playerEl = this.previewPlayer.el()
        if (playerEl) {
          return playerEl.querySelector('video') || playerEl
        }
      }
      const container = this.$refs.textOverlayPreview
      if (!container) {
        return null
      }
      return container.querySelector('video')
    },
    getPreviewCurrentTime () {
      if (this.previewPlayer && typeof this.previewPlayer.currentTime === 'function') {
        return this.previewPlayer.currentTime()
      }
      return 0
    },
    updatePreviewVideoSizeFromPlayer (player) {
      const playerWidth = player && player.videoWidth ? player.videoWidth() : 0
      const playerHeight = player && player.videoHeight ? player.videoHeight() : 0
      if (playerWidth && playerHeight) {
        this.previewVideoWidth = playerWidth
        this.previewVideoHeight = playerHeight
        return
      }
      const videoEl = this.getPreviewVideoEl()
      if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
        this.previewVideoWidth = videoEl.videoWidth
        this.previewVideoHeight = videoEl.videoHeight
      }
    },
    updatePreviewDisplayMetrics () {
      const container = this.$refs.textOverlayPreview
      const videoEl = this.getPreviewVideoEl()
      if (!container || !videoEl) {
        return
      }
      const containerRect = container.getBoundingClientRect()
      const videoRect = videoEl.getBoundingClientRect()
      const offsetX = videoRect.left - containerRect.left
      const offsetY = videoRect.top - containerRect.top
      this.previewDisplayOffsetX = Math.round(offsetX)
      this.previewDisplayOffsetY = Math.round(offsetY)
      if (this.previewVideoWidth > 0 && this.previewVideoHeight > 0) {
        this.previewDisplayScaleX = videoRect.width / this.previewVideoWidth
        this.previewDisplayScaleY = videoRect.height / this.previewVideoHeight
      } else {
        this.previewDisplayScaleX = 1
        this.previewDisplayScaleY = 1
      }
    },
    toRgba (hex, alphaPercent) {
      const normalized = (hex || '#000000').replace('#', '')
      const fullHex = normalized.length === 3
        ? normalized.split('').map((char) => char + char).join('')
        : normalized
      const r = parseInt(fullHex.slice(0, 2), 16) || 0
      const g = parseInt(fullHex.slice(2, 4), 16) || 0
      const b = parseInt(fullHex.slice(4, 6), 16) || 0
      const a = Math.max(0, Math.min(1, Number(alphaPercent) / 100))
      return `rgba(${r}, ${g}, ${b}, ${a})`
    },
    normalizeFontFamily (fontFamily) {
      const trimmed = String(fontFamily || '').trim()
      if (!this.fontOptions.length) {
        return trimmed
      }
      if (!trimmed || !this.fontOptions.includes(trimmed)) {
        return this.fontOptions[0]
      }
      return trimmed
    },
    ensureFontFaces () {
      if (typeof document === 'undefined') {
        return
      }
      if (document.getElementById('text-overlay-font-faces')) {
        return
      }
      const styleEl = document.createElement('style')
      styleEl.id = 'text-overlay-font-faces'
      styleEl.textContent = this.fontOptions.map((fontName) => {
        const encodedName = encodeURIComponent(fontName)
        return `@font-face { font-family: "${fontName}"; src: url("/fonts/${encodedName}"); font-display: swap; }`
      }).join('\n')
      document.head.appendChild(styleEl)
    }
  }
}
</script>

<style scoped>
</style>
